import { Buildings, MapPin, Phone } from "@phosphor-icons/react"
import { Info } from "@phosphor-icons/react/dist/ssr"
import {
  CodenameTag,
  Divider,
  FormQuerySelector,
  Tag,
} from "@vesatogo/grass-core"
import classNames from "classnames"
import { cloneDeep, get, set } from "lodash-es"
import { useEffect } from "react"
import { Link } from "react-router-dom"
import { FormCard } from "~/components/FormCard"
import SharedEntityGroup from "~/constants/SharedEntityGroup"
import {
  useAddUpdateSharedEntityMutation,
  useInvoiceSettlementAggregateQuery,
  useUsersQuery,
} from "~/generated/graphql"

type SharedEntity = {
  group: SharedEntityGroup
}

export type BuyerDetailsProps = {
  form
  inputProps?: any
  isDisabled
}

export const BuyerDetails = ({
  form,
  // inputProps,
  isDisabled,
}: BuyerDetailsProps) => {
  const buyer = form?.values?.user
  const [{ data }] = useInvoiceSettlementAggregateQuery({
    variables: {
      where: {
        buyer: {
          id: {
            _eq: form?.values?.user?.shared_id,
          },
        },
      },
    },
    pause: !form?.values?.user?.shared_id,
  })
  const isNewUser = data?.invoice_aggregate?.aggregate?.count === 0
  const [, upsertSharedEntity] = useAddUpdateSharedEntityMutation()

  useEffect(() => {
    form.setValues({ isNew: isNewUser })
  }, [data])

  function inputProps(name: string, sharedEntity?: SharedEntity) {
    const state = form.values
    return {
      value: get(state, name, ""),
      onChange: async (e: any) => {
        const stateCopy = cloneDeep(state)

        if (sharedEntity && e?.codename && e?.name && !e?.shared_id) {
          const { data } = await upsertSharedEntity({
            reference_id: e.codename,
            group_name: sharedEntity.group,
            name: e.name,
          })
          set(stateCopy, name, {
            ...e,
            shared_id: data?.insert_setu_trade_shared_entity_one?.id,
          })
        } else {
          set(stateCopy, name, e?.target ? e.target.value : e)
        }
        form.setValues(stateCopy)
      },
    }
  }

  const companies = buyer?.companies
  const business = form.values?.user?.my_businesses?.[0]
  return (
    <FormCard
      title="Buyer Details"
      action={
        <Link
          hidden={!form?.values?.id || !business?.id}
          className="text-blue-300 underline text-sm"
          target="_blank"
          to={{
            pathname:
              import.meta.env.VITE_KITE_UI +
              "/admin/d/business/" +
              business?.id,
          }}
        >
          Registration Details
        </Link>
      }
    >
      <div className={"grid grid-cols-3"}>
        <FormQuerySelector
          serverSideQuery
          label="Performed By"
          isDisabled={isDisabled}
          dataHook={useUsersQuery}
          variables={{ groups: ["Trader"] }}
          getOptionLabel={option => {
            if (option?.id === buyer?.id) {
              return option?.name
            }
            return (
              <>
                <div>{option?.name}</div>
                <div className="flex justify-between">
                  <CodenameTag
                    referenceId={
                      option?.external_reference_id2 ||
                      option?.external_reference_id ||
                      "na"
                    }
                  ></CodenameTag>
                  <div className="text-gray-600">{"+91 " + option?.number}</div>
                </div>
              </>
            )
          }}
          {...inputProps("user", {
            group: SharedEntityGroup.KiteUser,
          })}
        />
      </div>
      <section
        className={classNames(
          "p-2 bg-gray-100 mt-2 rounded-lg text-sm",
          buyer ? "" : "hidden"
        )}
      >
        <div className="flex gap-1 items-center text-gray-800 font-600">
          <Info className="text-blue-300" size={20} weight="fill" />
          <p> Summary</p>
        </div>
        <div className="ml-6 flex gap-2">
          <div>
            <Tag className="italic !rounded-xl" hidden={!isNewUser}>
              New!
            </Tag>
          </div>

          <div>
            <CodenameTag
              referenceId={
                buyer?.external_reference_id2 ||
                buyer?.external_reference_id ||
                "na"
              }
            ></CodenameTag>
          </div>

          {buyer?.number ? (
            <>
              <Divider />
              <div className="flex gap-1 items-center">
                <Phone className="text-blue-300" size={20} />
                <p>{"+91 " + buyer?.number}</p>
              </div>
            </>
          ) : (
            <></>
          )}
          {buyer?.current_address?.raw_address ? (
            <>
              <Divider />
              <div className="flex gap-1 items-center">
                <MapPin className="text-blue-300" size={20} />
                <p>{buyer?.current_address?.raw_address}</p>
              </div>
            </>
          ) : (
            <></>
          )}
          {companies?.length ? (
            <>
              <Divider />
              <div
                className="flex gap-1 items-center"
                hidden={!companies?.length}
              >
                <Buildings className="text-blue-300" size={20} />
                <p>
                  {companies?.map((com, idx) => {
                    return idx === companies?.length - 1
                      ? com?.company?.display_name
                      : com?.company?.display_name + ", "
                  })}
                </p>
              </div>
            </>
          ) : (
            <></>
          )}
        </div>
      </section>
    </FormCard>
  )
}
